<script setup>
import { formatDate } from '@core/utils/formatters'
import ConfirmDialog from '@/components/shared/ConfirmDialog.vue'
import { classificationLabel } from '@/stores/appointments'
import {
  appointmentStatusColor,
  appointmentStatusLabel,
  bookingBlockedMessage,
  sessionFormatColor,
  sessionFormatLabel,
  useClientBookingStore,
} from '@/stores/clientBooking'

definePage({
  meta: {
    layout: 'client',
    clientArea: true,
  },
})

const store = useClientBookingStore()
const { t } = useI18n()

const formError = ref('')
const formSuccess = ref('')
const confirmDialog = ref(false)
const selectedSlot = ref(null)
const selectedSessionFormat = ref(null)
const formatError = ref('')
const cancelDialog = ref(false)
const selectedAppointment = ref(null)
const cancelPreview = ref(null)
const cancelLoading = ref(false)


const localDateKey = isoOrDate => {
  const d = new Date(isoOrDate)
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')

  return `${y}-${m}-${day}`
}

const weekDays = computed(() => {
  const start = new Date(store.weekStart)

  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(start)

    d.setDate(d.getDate() + i)

    return {
      date: d,
      key: localDateKey(d),
      label: formatDate(d, { weekday: 'short', day: 'numeric', month: 'short' }, 'nl'),
    }
  })
})

const slotsForDay = key => {
  return (store.timeSlots || [])
    .filter(s => localDateKey(s.starts_at) === key)
    .sort((a, b) => a.starts_at.localeCompare(b.starts_at))
}

const formatTimeRange = slot => {
  const opts = { hour: '2-digit', minute: '2-digit' }
  const start = formatDate(slot.starts_at, opts, 'nl')
  const end = formatDate(slot.ends_at, opts, 'nl')

  return `${start} – ${end}`
}

const formatAppointmentWhen = appointment => {
  const date = formatDate(appointment.starts_at, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }, 'nl')

  const time = formatTimeRange(appointment)

  return `${date}, ${time}`
}

const weekLabel = computed(() => {
  const start = weekDays.value[0]?.date
  const end = weekDays.value[6]?.date
  if (!start || !end)
    return ''

  return `${formatDate(start, { day: 'numeric', month: 'short' }, 'nl')} – ${formatDate(end, { day: 'numeric', month: 'short', year: 'numeric' }, 'nl')}`
})

const blockedMessage = computed(() => {
  if (store.bookingEligible || !store.bookingBlockedReason)
    return ''

  return bookingBlockedMessage(store.bookingBlockedReason)
})

const loadAvailability = async () => {
  formError.value = ''
  try {
    await store.fetchAvailability()
  }
  catch {
    formError.value = store.error || t('clientBooking.blocked.generic')
  }
}

const loadAppointments = async () => {
  try {
    await store.fetchAppointments()
  }
  catch {
    formError.value = store.error || t('clientBooking.blocked.generic')
  }
}

onMounted(async () => {
  await Promise.all([loadAvailability(), loadAppointments()])
})

watch(() => store.weekStart, () => {
  loadAvailability()
})

const openConfirm = slot => {
  if (!store.bookingEligible)
    return
  selectedSlot.value = slot
  selectedSessionFormat.value = null
  formatError.value = ''
  formError.value = ''
  confirmDialog.value = true
}

const submitBooking = async () => {
  if (!selectedSlot.value)
    return

  if (!selectedSessionFormat.value) {
    formatError.value = t('clientBooking.formatRequired')

    return
  }

  formError.value = ''
  formSuccess.value = ''
  formatError.value = ''
  try {
    await store.bookSlot(selectedSlot.value.id, selectedSessionFormat.value)
    formSuccess.value = t('clientBooking.bookedSuccess')
    confirmDialog.value = false
    selectedSlot.value = null
    selectedSessionFormat.value = null
  }
  catch (err) {
    formError.value = err.payload?.errors?.join(' ') || err.message
    confirmDialog.value = false
  }
}

const selectedDateLabel = computed(() => {
  if (!selectedSlot.value)
    return ''

  return formatDate(selectedSlot.value.starts_at, {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  }, 'nl')
})

const selectedTimeLabel = computed(() => {
  if (!selectedSlot.value)
    return ''

  return formatTimeRange(selectedSlot.value)
})

const canCancelAppointment = appointment => {
  return ['requested', 'confirmed', 'rescheduled'].includes(appointment?.status)
}

const openCancel = async appointment => {
  formError.value = ''
  selectedAppointment.value = appointment
  cancelPreview.value = null
  cancelLoading.value = true
  cancelDialog.value = true
  try {
    cancelPreview.value = await store.cancellationPreview(appointment.id)
  }
  catch (err) {
    formError.value = err.payload?.errors?.join(' ') || err.message
    cancelDialog.value = false
  }
  finally {
    cancelLoading.value = false
  }
}

const cancelBody = computed(() => {
  if (!selectedAppointment.value || !cancelPreview.value)
    return t('clientBooking.cancelLoadingBody')

  const classification = classificationLabel(cancelPreview.value.classification)

  const consume = cancelPreview.value.consumes_session
    ? t('clientBooking.cancelConsumesYes')
    : t('clientBooking.cancelConsumesNo')

  return t('clientBooking.cancelBody', {
    when: formatAppointmentWhen(selectedAppointment.value),
    classification,
    consume,
  })
})

const confirmCancel = async () => {
  if (!selectedAppointment.value)
    return

  formError.value = ''
  formSuccess.value = ''
  cancelLoading.value = true
  try {
    await store.cancelAppointment(selectedAppointment.value.id)
    formSuccess.value = t('clientBooking.cancelledSuccess')
    cancelDialog.value = false
    selectedAppointment.value = null
  }
  catch (err) {
    formError.value = err.payload?.errors?.join(' ') || err.message
  }
  finally {
    cancelLoading.value = false
  }
}
</script>

<template>
  <div
    class="pa-6 mx-auto"
    style="max-inline-size: 960px;"
  >
    <div class="mb-6">
      <h1 class="text-h4 mb-1">
        {{ $t('clientBooking.title') }}
      </h1>
      <p class="text-medium-emphasis mb-0">
        {{ $t('clientBooking.subtitle') }}
      </p>
    </div>

    <VAlert
      v-if="formError"
      type="error"
      variant="tonal"
      class="mb-4"
      role="alert"
    >
      {{ formError }}
    </VAlert>
    <VAlert
      v-if="formSuccess"
      type="success"
      variant="tonal"
      class="mb-4"
      role="status"
    >
      {{ formSuccess }}
    </VAlert>
    <VAlert
      v-if="blockedMessage"
      type="warning"
      variant="tonal"
      class="mb-4"
      role="alert"
    >
      {{ blockedMessage }}
    </VAlert>

    <VCard
      flat
      border
      class="mb-6"
    >
      <VCardItem>
        <VCardTitle>{{ $t('clientBooking.slotsTitle') }}</VCardTitle>
        <template #append>
          <div class="d-flex align-center ga-2 flex-wrap">
            <VBtn
              icon
              variant="text"
              size="small"
              :aria-label="$t('clientBooking.prevWeek')"
              @click="store.shiftWeek(-1)"
            >
              <VIcon icon="ri-arrow-left-s-line" />
            </VBtn>
            <span class="text-body-2">{{ weekLabel }}</span>
            <VBtn
              icon
              variant="text"
              size="small"
              :aria-label="$t('clientBooking.nextWeek')"
              @click="store.shiftWeek(1)"
            >
              <VIcon icon="ri-arrow-right-s-line" />
            </VBtn>
            <VBtn
              size="small"
              variant="tonal"
              @click="store.goToThisWeek()"
            >
              {{ $t('clientBooking.thisWeek') }}
            </VBtn>
          </div>
        </template>
      </VCardItem>
      <VCardText>
        <div
          v-if="store.loading"
          role="status"
        >
          {{ $t('clientBooking.loading') }}
        </div>
        <div
          v-else-if="store.isSlotsEmpty"
          class="text-medium-emphasis"
          role="status"
        >
          {{ $t('clientBooking.emptySlots') }}
        </div>
        <div
          v-else
          class="booking-week"
        >
          <section
            v-for="day in weekDays"
            :key="day.key"
            class="booking-day"
          >
            <h2 class="text-subtitle-2 mb-2">
              {{ day.label }}
            </h2>
            <ul
              v-if="slotsForDay(day.key).length"
              class="booking-slot-list"
            >
              <li
                v-for="slot in slotsForDay(day.key)"
                :key="slot.id"
              >
                <VBtn
                  block
                  variant="tonal"
                  color="success"
                  class="justify-start text-none"
                  :disabled="!store.bookingEligible || store.booking"
                  :aria-label="$t('clientBooking.slotAria', { time: formatTimeRange(slot), date: day.label })"
                  @click="openConfirm(slot)"
                >
                  {{ formatTimeRange(slot) }}
                </VBtn>
              </li>
            </ul>
            <p
              v-else
              class="text-caption text-medium-emphasis mb-0"
            >
              {{ $t('clientBooking.emptyDay') }}
            </p>
          </section>
        </div>
      </VCardText>
    </VCard>

    <VCard
      flat
      border
    >
      <VCardItem>
        <VCardTitle>{{ $t('clientBooking.appointmentsTitle') }}</VCardTitle>
      </VCardItem>
      <VCardText>
        <div
          v-if="store.appointmentsLoading"
          role="status"
        >
          {{ $t('clientBooking.loadingAppointments') }}
        </div>
        <div
          v-else-if="store.isAppointmentsEmpty"
          class="text-medium-emphasis"
          role="status"
        >
          {{ $t('clientBooking.emptyAppointments') }}
        </div>
        <template v-else>
          <h2 class="text-subtitle-1 mb-2">
            {{ $t('clientBooking.upcomingTitle') }}
          </h2>
          <VList
            v-if="store.upcoming.length"
            class="mb-6"
            lines="two"
          >
            <VListItem
              v-for="appointment in store.upcoming"
              :key="appointment.id"
            >
              <VListItemTitle>{{ formatAppointmentWhen(appointment) }}</VListItemTitle>
              <VListItemSubtitle>
                <span class="me-2">
                  {{ $t('clientBooking.statusPrefix') }}
                  <VChip
                    size="x-small"
                    label
                    variant="tonal"
                    class="ms-1"
                    :color="appointmentStatusColor(appointment.status)"
                  >
                    {{ appointmentStatusLabel(appointment.status) }}
                  </VChip>
                </span>
                <span>
                  {{ $t('sessionFormat.prefix') }}
                  <VChip
                    size="x-small"
                    label
                    variant="tonal"
                    class="ms-1"
                    :color="sessionFormatColor(appointment.session_format)"
                  >
                    {{ sessionFormatLabel(appointment.session_format) }}
                  </VChip>
                </span>
              </VListItemSubtitle>
              <template
                v-if="canCancelAppointment(appointment)"
                #append
              >
                <VBtn
                  size="small"
                  variant="text"
                  color="error"
                  :disabled="store.booking || cancelLoading"
                  :aria-label="$t('clientBooking.cancelAria', { when: formatAppointmentWhen(appointment) })"
                  @click="openCancel(appointment)"
                >
                  {{ $t('clientBooking.cancelAppointment') }}
                </VBtn>
              </template>
            </VListItem>
          </VList>
          <p
            v-else
            class="text-medium-emphasis mb-6"
          >
            —
          </p>

          <h2 class="text-subtitle-1 mb-2">
            {{ $t('clientBooking.pastTitle') }}
          </h2>
          <VList
            v-if="store.past.length"
            lines="two"
          >
            <VListItem
              v-for="appointment in store.past"
              :key="appointment.id"
            >
              <VListItemTitle>{{ formatAppointmentWhen(appointment) }}</VListItemTitle>
              <VListItemSubtitle>
                <span class="me-2">
                  {{ $t('clientBooking.statusPrefix') }}
                  <VChip
                    size="x-small"
                    label
                    variant="tonal"
                    class="ms-1"
                    :color="appointmentStatusColor(appointment.status)"
                  >
                    {{ appointmentStatusLabel(appointment.status) }}
                  </VChip>
                </span>
                <span>
                  {{ $t('sessionFormat.prefix') }}
                  <VChip
                    size="x-small"
                    label
                    variant="tonal"
                    class="ms-1"
                    :color="sessionFormatColor(appointment.session_format)"
                  >
                    {{ sessionFormatLabel(appointment.session_format) }}
                  </VChip>
                </span>
              </VListItemSubtitle>
            </VListItem>
          </VList>
          <p
            v-else
            class="text-medium-emphasis mb-0"
          >
            —
          </p>
        </template>
      </VCardText>
    </VCard>

    <VDialog
      v-model="confirmDialog"
      max-width="480"
      aria-modal="true"
    >
      <VCard>
        <VCardItem>
          <VCardTitle>{{ $t('clientBooking.confirmTitle') }}</VCardTitle>
        </VCardItem>
        <VCardText>
          <p class="mb-4">
            {{ $t('clientBooking.confirmBody', { time: selectedTimeLabel, date: selectedDateLabel }) }}
          </p>
          <fieldset class="booking-format-fieldset">
            <legend
              id="booking-session-format-legend"
              class="text-subtitle-2 mb-2"
            >
              {{ $t('clientBooking.formatLegend') }}
            </legend>
            <p class="text-caption text-medium-emphasis mb-3">
              {{ $t('clientBooking.formatHint') }}
            </p>
            <VRadioGroup
              v-model="selectedSessionFormat"
              aria-labelledby="booking-session-format-legend"
              :error-messages="formatError ? [formatError] : []"
              hide-details="auto"
            >
              <VRadio
                value="physical"
                :label="$t('sessionFormat.physical')"
              />
              <VRadio
                value="online"
                :label="$t('sessionFormat.online')"
              />
            </VRadioGroup>
          </fieldset>
        </VCardText>
        <VCardActions>
          <VSpacer />
          <VBtn
            variant="text"
            @click="confirmDialog = false"
          >
            {{ $t('clientBooking.cancel') }}
          </VBtn>
          <VBtn
            color="primary"
            :loading="store.booking"
            :disabled="!selectedSessionFormat"
            @click="submitBooking"
          >
            {{ $t('clientBooking.confirmAction') }}
          </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>

    <ConfirmDialog
      v-model="cancelDialog"
      :title="$t('clientBooking.cancelTitle')"
      :body="cancelBody"
      :confirm-label="$t('clientBooking.cancelConfirm')"
      :cancel-label="$t('common.cancel')"
      color="error"
      :loading="cancelLoading || store.booking"
      @confirm="confirmCancel"
    />
  </div>
</template>

<style scoped>
.booking-week {
  display: grid;
  gap: 1rem;
  grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
}

.booking-slot-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding: 0;
  margin: 0;
  list-style: none;
}

.booking-format-fieldset {
  margin: 0;
  padding: 0;
  border: 0;
  min-inline-size: 0;
}
</style>
